import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectToken } from 'src/app/store/selectors/auth.selectors';
import { environment } from 'src/environments/environment';

export interface AiReply {
  main?: string;
  clarification?: string;
  clarification_options?: string[];
  disclaimer?: string;
  follow_up_questions?: string[];
  sources?: AiSource[];
  contact_cse_recommended?: boolean;
}

export interface AiSource {
  doc_uuid?: string;
  title?: string;
  doc_title?: string;
  source_url?: string;
  viewer_url?: string;
  highlight_text?: string;
  article_ref?: string;
  page?: number;
  char_start?: number;
  char_end?: number;
  content?: string;
  excerpt?: string;
  snippet?: string;
  text?: string;
  similarity?: number;
}

export interface AiChatResponse {
  conversation_id?: number;
  message_id?: number;
  content?: string;
  reply?: AiReply;
  sources?: AiSource[];
}

export type AiStreamEventName = 'meta' | 'text' | 'structured' | 'done' | 'error';

export interface AiStreamEvent {
  event: AiStreamEventName;
  data: {
    conversation_id?: number;
    text?: string;
    part?: 'main';
    reply?: AiReply;
    rag_sources?: AiSource[];
    status?: string;
    message?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  constructor(
    private httpClient: HttpClient,
    private store: Store,
  ) {}

  ask(question: string, domainUuid?: string, conversationId?: number): Observable<AiChatResponse> {
    return this.httpClient.post<AiChatResponse>(`${environment.apiUrl}/ai/chat`, {
      question,
      domain_uuid: domainUuid || undefined,
      conversation_id: conversationId || undefined,
    });
  }

  stream(question: string, domainUuid?: string, conversationId?: number): Observable<AiStreamEvent> {
    return new Observable<AiStreamEvent>((subscriber) => {
      const abortController = new AbortController();

      this.consumeStream(question, domainUuid, conversationId, abortController, (event) => {
        subscriber.next(event);
      }).then(() => {
        subscriber.complete();
      }).catch((error) => {
        if (!subscriber.closed && error?.name !== 'AbortError') {
          subscriber.error(error);
        }
      });

      return () => abortController.abort();
    });
  }

  private async consumeStream(
    question: string,
    domainUuid: string | undefined,
    conversationId: number | undefined,
    abortController: AbortController,
    emit: (event: AiStreamEvent) => void,
  ): Promise<void> {
    const token = await this.store.select(selectToken).pipe(take(1)).toPromise();
    const storedDomain = await Preferences.get({ key: `${environment.application}-domain` });
    const response = await fetch(`${environment.apiUrl}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
        Application: environment.application,
        Domain: storedDomain.value || domainUuid || '',
        IsMobile: 'true',
      },
      body: JSON.stringify({
        question,
        domain_uuid: domainUuid || undefined,
        conversation_id: conversationId || undefined,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      let payload: any = {};
      try {
        payload = await response.json();
      } catch (_) {}
      const error: any = new Error(payload?.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.error = payload;
      throw error;
    }

    if (!response.body) {
      this.parseSseBuffer(await response.text(), emit, true);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      buffer = this.parseSseBuffer(buffer, emit, done);
      if (done) {
        break;
      }
    }
  }

  private parseSseBuffer(
    buffer: string,
    emit: (event: AiStreamEvent) => void,
    flush: boolean,
  ): string {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const blocks = normalized.split('\n\n');
    const remainder = flush ? '' : (blocks.pop() || '');

    blocks.forEach((block) => this.emitSseBlock(block, emit));
    if (flush && blocks.length === 0 && normalized.trim()) {
      this.emitSseBlock(normalized, emit);
    }

    return remainder;
  }

  private emitSseBlock(block: string, emit: (event: AiStreamEvent) => void): void {
    if (!block.trim()) {
      return;
    }

    const lines = block.split('\n');
    const event = lines.find(line => line.startsWith('event:'))?.slice(6).trim() as AiStreamEventName;
    const data = lines
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).replace(/^\s+/, ''))
      .join('\n');

    if (!event || !data) {
      return;
    }

    try {
      emit({ event, data: JSON.parse(data) });
    } catch (_) {}
  }
}
